import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { RADIUS, TEXT_MUTED, TEXT_PRIMARY, OPAQUE_PANEL_BG, GOLD_TEXT } from '../../lib/medievalTheme';
import { getClass } from '../../utils/arena/classes';
import { OMENS, omenProgress, omenText, rewardLabel } from '../../utils/arena/oracle';
import { t } from '../../localization/t';
import { VialGauge } from './ForesightGauge';
import TarotCard from './TarotCard';

const ACCENT = getClass('oracle').accent;
const DONE = '#8fd3a4';

// The kept omen during the match: its plate, its name and a vial filling
// toward the ask, read from the mirror's `stats`. Sits at the top edge of
// the board; hovering it lays the card out again to reread the reading.
export default class OmenProgress extends Component {
  state = { open: false };

  render() {
    const { omen, stats, seat } = this.props;
    const def = OMENS[omen?.id];
    if (!def) return null;
    const { value, target, done } = omenProgress(omen, stats, seat);
    const tint = done ? DONE : ACCENT;
    // The insight has no count to fill toward: the vial is full while no
    // damage has landed, and empties the moment it does.
    const gaugeValue = def.insight ? (done ? 1 : 0) : Math.min(value, target);
    const gaugeMax = def.insight ? 1 : target;
    return (
      <div
        className="fixed top-3 left-1/2 -translate-x-1/2 z-[999]"
        onMouseEnter={() => this.setState({ open: true })}
        onMouseLeave={() => this.setState({ open: false })}
        data-testid="omen-progress"
      >
        <div
          className="flex items-center"
          style={{
            gap: '0.625cqw', padding: '0.3125cqw 0.875cqw 0.3125cqw 0.3125cqw', width: '15cqw',
            borderRadius: RADIUS.md, backgroundColor: OPAQUE_PANEL_BG,
            boxShadow: `0 0 0 1px ${done ? DONE : `${GOLD_TEXT} 0.45)`}, 0 0.25cqw 0.75cqw rgba(0,0,0,0.6)`,
            transitionProperty: 'box-shadow', transitionDuration: '300ms',
          }}
        >
          <img src={def.art} alt="" draggable={false} style={{ width: '1.75cqw', aspectRatio: '480 / 780', objectFit: 'cover', borderRadius: RADIUS.xs, boxShadow: '0 0 0 1px rgba(255,255,255,0.14)' }} />
          <div className="flex flex-col flex-1 min-w-0" style={{ gap: '0.25cqw' }}>
            <div className="flex items-baseline justify-between" style={{ gap: '0.5cqw' }}>
              <span className="arena-heading truncate" style={{ color: tint, fontSize: '0.8125cqw', letterSpacing: '0.04em' }}>{t(def.title)}</span>
              <span className="arena-heading tabular-nums" style={{ color: done ? DONE : TEXT_MUTED, fontSize: '0.6875cqw' }}>
                {def.insight ? (done ? t('Untouched') : t('{n} taken', { n: value })) : done ? t('Fulfilled') : `${value} / ${target}`}
              </span>
            </div>
            <VialGauge value={gaugeValue} max={gaugeMax} color={tint} height={0.625} notch={def.insight ? 0 : (target > 12 ? 5 : 1)} />
          </div>
        </div>
        <AnimatePresence>
          {this.state.open ? (
            <motion.div
              className="absolute left-1/2"
              style={{ top: '100%', marginTop: '0.625cqw', x: '-50%' }}
              initial={{ opacity: 0, y: '-0.5cqw', x: '-50%' }}
              animate={{ opacity: 1, y: 0, x: '-50%' }}
              exit={{ opacity: 0, y: '-0.375cqw', x: '-50%' }}
              transition={{ type: 'spring', duration: 0.3, bounce: 0 }}
            >
              <TarotCard
                art={def.art}
                name={t(def.title)}
                width={12}
                gilded={omen.tier === 2}
                tag={omen.tier === 2 ? t('Greater omen') : null}
                caption={(
                  <>
                    <div>{omenText(omen)}</div>
                    <div style={{ color: TEXT_PRIMARY, marginTop: '0.3cqw', opacity: 0.85 }}>{rewardLabel(omen.reward)}</div>
                  </>
                )}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }
}
